import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    getScopeLabel,
    getReviewStatusLabel,
    getTaskStatusLabel,
    getTaskTypeLabel,
    getRouteStatusLabel,
    getEvidenceStatusLabel,
    PMO_METRIC_LABELS,
    PMO_SCOPE_LABELS,
} from "../utils/pmoLabels";
import {
    PMO_BADGE_TONES,
    getPmoTooltip,
    getTaskStatusTone,
    getReviewStatusTone,
    getRouteStatusTone,
    getEvidenceStatusTone,
    getHealthTone,
} from "../utils/pmoStyles";
import { API_BASE } from "../config/orbisConfig";



function safeNumber(value, fallback = 0) {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
}

function pct(value) {
    return Math.max(0, Math.min(100, safeNumber(value)));
}

function cleanText(value) {
    return String(value || "")
        .replaceAll("atenci�n", "atención")
        .replaceAll("Atenci�n", "Atención")
        .replaceAll("Presi�n", "Presión")
        .replaceAll("presi�n", "presión");
}

function label(value) {
    return String(value || "sin estado")
        .replaceAll("_", " ")
        .replaceAll("-", " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatDate(value) {
    if (!value) return "Sin fecha";
    const d = new Date(`${value}T00:00:00`);
    if (Number.isNaN(d.getTime())) return value;

    return d.toLocaleDateString("es-MX", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
}

function initials(name = "Usuario") {
    return String(name)
        .trim()
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((x) => x[0]?.toUpperCase())
        .join("") || "U";
}

function statusStyle(status) {
    const s = String(status || "").toLowerCase();

    if (s === "verde") {
        return { color: "#047857", bg: "#ECFDF5", border: "#A7F3D0", accent: "#16A34A" };
    }

    if (s === "amarillo") {
        return { color: "#B45309", bg: "#FFFBEB", border: "#FDE68A", accent: "#F59E0B" };
    }

    if (s === "rojo") {
        return { color: "#BE123C", bg: "#FFF1F2", border: "#FECDD3", accent: "#DC2626" };
    }

    return { color: "#475569", bg: "#F8FAFC", border: "#E2E8F0", accent: "#64748B" };
}

function priorityStyle(priority) {
    const p = String(priority || "").toLowerCase();

    if (p === "critica") return { bg: "#FFF1F2", color: "#BE123C", border: "#FECDD3" };
    if (p === "alta") return { bg: "#FFFBEB", color: "#B45309", border: "#FDE68A" };
    if (p === "media") return { bg: "#EFF6FF", color: "#1D4ED8", border: "#BFDBFE" };

    return { bg: "#F8FAFC", color: "#475569", border: "#E2E8F0" };
}

function KpiCard({ title, value, subtitle, color = "#0F172A", icon = "◈" }) {
    return (
        <div
            style={{
                borderRadius: 24,
                background: "#FFFFFF",
                border: "1px solid #E2E8F0",
                padding: 18,
                boxShadow: "0 18px 38px rgba(15,23,42,.06)",
                minHeight: 132,
                position: "relative",
                overflow: "hidden",
            }}
        >
            <div
                style={{
                    position: "absolute",
                    right: -34,
                    top: -34,
                    width: 110,
                    height: 110,
                    borderRadius: 999,
                    background: `${color}13`,
                }}
            />

            <div style={{ position: "relative", display: "flex", justifyContent: "space-between", gap: 12 }}>
                <div>
                    <div
                        style={{
                            color: "#64748B",
                            fontSize: 11,
                            fontWeight: 950,
                            textTransform: "uppercase",
                            letterSpacing: ".04em",
                        }}
                    >
                        {title}
                    </div>

                    <div
                        style={{
                            marginTop: 10,
                            color,
                            fontSize: 34,
                            fontWeight: 950,
                            letterSpacing: "-.05em",
                            lineHeight: 1,
                        }}
                    >
                        {value}
                    </div>

                    <div
                        style={{
                            marginTop: 10,
                            color: "#64748B",
                            fontSize: 12,
                            fontWeight: 750,
                            lineHeight: 1.35,
                        }}
                    >
                        {subtitle}
                    </div>
                </div>

                <div
                    style={{
                        width: 44,
                        height: 44,
                        borderRadius: 16,
                        background: `${color}12`,
                        color,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 18,
                        fontWeight: 950,
                        border: `1px solid ${color}22`,
                    }}
                >
                    {icon}
                </div>
            </div>
        </div>
    );
}

function ProgressBar({ value, color = "#2563EB", height = 10 }) {
    const v = pct(value);

    return (
        <div
            style={{
                height,
                borderRadius: 999,
                background: "#E2E8F0",
                overflow: "hidden",
                border: "1px solid #E2E8F0",
            }}
        >
            <div
                style={{
                    width: `${v}%`,
                    height: "100%",
                    borderRadius: 999,
                    background: color,
                    boxShadow: `0 8px 18px ${color}40`,
                }}
            />
        </div>
    );
}

function UserAvatar({ name, status }) {
    const s = statusStyle(status);

    return (
        <div
            style={{
                width: 42,
                height: 42,
                borderRadius: 16,
                background: `linear-gradient(135deg, ${s.accent}, #0F172A)`,
                color: "#FFFFFF",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 12,
                fontWeight: 950,
                boxShadow: `0 14px 28px ${s.accent}24`,
                flex: "0 0 auto",
            }}
        >
            {initials(name)}
        </div>
    );
}

function ReliabilityCard({ person }) {
    const s = statusStyle(person.reliability_status);
    const reliability = safeNumber(person.reliability_index);

    return (
        <div
            style={{
                borderRadius: 24,
                background: "#FFFFFF",
                border: `1px solid ${s.border}`,
                boxShadow: "0 18px 45px rgba(15,23,42,.06)",
                padding: 18,
            }}
        >
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
                <div style={{ display: "flex", gap: 12, alignItems: "center", minWidth: 0 }}>
                    <UserAvatar name={person.display_name} status={person.reliability_status} />
                    <div style={{ minWidth: 0 }}>
                        <div
                            style={{
                                color: "#0F172A",
                                fontSize: 15,
                                fontWeight: 950,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            }}
                        >
                            {person.display_name || "Sin nombre"}
                        </div>
                        <div style={{ marginTop: 3, color: "#64748B", fontSize: 11, fontWeight: 800 }}>
                            {person.assigned_tasks || 0} asignadas · {person.completed_tasks || 0} terminadas
                        </div>
                    </div>
                </div>

                <div
                    style={{
                        borderRadius: 16,
                        background: s.bg,
                        border: `1px solid ${s.border}`,
                        color: s.color,
                        padding: "8px 10px",
                        textAlign: "center",
                        minWidth: 76,
                    }}
                >
                    <div style={{ fontSize: 22, fontWeight: 950, letterSpacing: "-.04em" }}>
                        {reliability.toFixed(0)}
                    </div>
                    <div style={{ fontSize: 9, fontWeight: 950, textTransform: "uppercase" }}>RI</div>
                </div>
            </div>

            <div style={{ marginTop: 14 }}>
                <ProgressBar value={reliability} color={s.accent} />
            </div>

            <div
                style={{
                    marginTop: 12,
                    display: "grid",
                    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                    gap: 8,
                }}
            >
                <MiniMetric label="Cierre" value={`${safeNumber(person.completion_rate).toFixed(0)}%`} />
                <MiniMetric label="Vencidas" value={person.overdue_tasks || 0} danger={safeNumber(person.overdue_tasks) > 0} />
                <MiniMetric label="Evidencia" value={`${safeNumber(person.evidence_rate).toFixed(0)}%`} />
                <MiniMetric label="Incons." value={person.inconsistent_tasks || 0} danger={safeNumber(person.inconsistent_tasks) > 0} />
            </div>

            <div
                style={{
                    marginTop: 12,
                    borderRadius: 16,
                    background: s.bg,
                    border: `1px solid ${s.border}`,
                    color: s.color,
                    padding: 10,
                    fontSize: 12,
                    fontWeight: 900,
                    lineHeight: 1.35,
                }}
            >
                {cleanText(person.reliability_label || "Sin lectura")}
            </div>
        </div>
    );
}


function LegendPill({ color, label }) {
    return (
        <span
            style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 10px",
                borderRadius: 999,
                border: "1px solid #E2E8F0",
                background: "#FFFFFF",
                color: "#334155",
                fontSize: 11,
                fontWeight: 850,
                whiteSpace: "nowrap",
            }}
        >
            <span
                style={{
                    width: 8,
                    height: 8,
                    borderRadius: 999,
                    background: color,
                    display: "inline-block",
                }}
            />
            {label}
        </span>
    );
}

function ReliabilityHorizontalChart({ items = [] }) {
    const sortedItems = [...items].sort(
        (a, b) => safeNumber(b.reliability_index) - safeNumber(a.reliability_index)
    );

    return (
        <div
            style={{
                borderRadius: 28,
                background: "rgba(255,255,255,.92)",
                border: "1px solid #E2E8F0",
                padding: 18,
                boxShadow: "0 18px 45px rgba(15,23,42,.06)",
            }}
        >
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 12,
                    alignItems: "flex-start",
                    marginBottom: 18,
                    flexWrap: "wrap",
                }}
            >
                <SectionTitle
                    title="Reliability Index por responsable"
                    subtitle="Comparativo horizontal de confiabilidad operativa: cierre, vencimientos, evidencia e inconsistencias."
                />

                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <LegendPill color="#16A34A" label="Alta" />
                    <LegendPill color="#F59E0B" label="Media" />
                    <LegendPill color="#DC2626" label="Atención" />
                </div>
            </div>

            {sortedItems.length ? (
                <div style={{ display: "grid", gap: 16 }}>
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "minmax(190px, 260px) minmax(220px, 1fr) 82px",
                            gap: 14,
                            alignItems: "center",
                            color: "#94A3B8",
                            fontSize: 10,
                            fontWeight: 900,
                            padding: "0 12px",
                        }}
                    >
                        <div />
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <span>0</span>
                            <span>50</span>
                            <span>100</span>
                        </div>
                        <div />
                    </div>

                    {sortedItems.map((person) => {
                        const s = statusStyle(person.reliability_status);
                        const reliability = safeNumber(person.reliability_index);
                        const width = pct(reliability);

                        return (
                            <div
                                key={person.user_id || person.display_name}
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "minmax(210px, 280px) minmax(260px, 1fr) 120px",
                                    gap: 16,
                                    alignItems: "center",
                                    padding: 12,
                                    borderRadius: 18,
                                    background: "#FFFFFF",
                                    border: `1px solid ${s.border}`,
                                }}
                            >

                                <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                                    <UserAvatar name={person.display_name} status={person.reliability_status} />
                                    <div style={{ minWidth: 0 }}>
                                        <div
                                            style={{
                                                color: "#0F172A",
                                                fontSize: 13,
                                                fontWeight: 950,
                                                whiteSpace: "nowrap",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                            }}
                                        >
                                            {person.display_name || "Sin nombre"}
                                        </div>
                                        <div
                                            style={{
                                                marginTop: 3,
                                                color: "#64748B",
                                                fontSize: 11,
                                                fontWeight: 800,
                                                whiteSpace: "nowrap",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                            }}
                                        >
                                            {person.assigned_tasks || 0} asignadas · {person.completed_tasks || 0} terminadas
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <div
                                        style={{
                                            height: 20,
                                            borderRadius: 999,
                                            background: "#E2E8F0",
                                            overflow: "hidden",
                                            border: "1px solid #E2E8F0",
                                            position: "relative",
                                        }}
                                    >
                                        <div
                                            style={{
                                                width: `${width}%`,
                                                height: "100%",
                                                borderRadius: 999,
                                                background: s.accent,
                                                boxShadow: `0 8px 18px ${s.accent}40`,
                                                transition: "width .35s ease",
                                            }}
                                        />
                                    </div>

                                    <div
                                        style={{
                                            marginTop: 8,
                                            display: "grid",
                                            gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                                            gap: 8,
                                        }}
                                    >
                                        <MiniMetric label="Cierre" value={`${safeNumber(person.completion_rate).toFixed(0)}%`} />
                                        <MiniMetric label="Venc." value={person.overdue_tasks || 0} danger={safeNumber(person.overdue_tasks) > 0} />
                                        <MiniMetric label="Evid." value={`${safeNumber(person.evidence_rate).toFixed(0)}%`} />
                                        <MiniMetric label="Incons." value={person.inconsistent_tasks || 0} danger={safeNumber(person.inconsistent_tasks) > 0} />
                                    </div>
                                </div>

                                <div style={{ textAlign: "right" }}>
                                    <div
                                        style={{
                                            color: s.accent,
                                            fontSize: 22,
                                            fontWeight: 950,
                                            letterSpacing: "-.04em",
                                            lineHeight: 1,
                                        }}
                                    >
                                        {reliability.toFixed(1)}
                                    </div>
                                    <div
                                        style={{
                                            marginTop: 4,
                                            color: s.color,
                                            fontSize: 10,
                                            fontWeight: 950,
                                            textTransform: "uppercase",
                                        }}
                                    >
                                        {cleanText(person.reliability_label || "RI /100")}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                <EmptyBox text="No hay responsables con tareas asignadas para este filtro." />
            )}
        </div>
    );
}

function ReviewerCard({ reviewer, onOpenReviewerTasks }) {
    const s = statusStyle(reviewer.pressure_status);

    return (
        <div
            style={{
                borderRadius: 22,
                background: "#FFFFFF",
                border: `1px solid ${s.border}`,
                padding: 16,
                boxShadow: "0 14px 32px rgba(15,23,42,.05)",
            }}
        >
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                    <UserAvatar name={reviewer.display_name} status={reviewer.pressure_status} />
                    <div style={{ minWidth: 0 }}>
                        <div
                            style={{
                                color: "#0F172A",
                                fontSize: 14,
                                fontWeight: 950,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            }}
                        >
                            {reviewer.display_name || "Sin revisor"}
                        </div>
                        <div style={{ color: "#64748B", fontSize: 11, fontWeight: 800, marginTop: 3 }}>
                            {reviewer.review_assignments || 0} revisiones asignadas
                        </div>
                    </div>
                </div>

                <div
                    title="Review Pressure Score: 0–30 flujo estable · 31–79 presión moderada · 80+ cuello de botella crítico"
                    style={{
                        color: s.color,
                        fontSize: 22,
                        fontWeight: 950,
                        cursor: "help",
                    }}
                >
                    {safeNumber(reviewer.review_pressure_score).toFixed(0)}
                </div>
            </div>

            <div style={{ marginTop: 14 }}>
                <ProgressBar value={Math.min(100, safeNumber(reviewer.review_pressure_score))} color={s.accent} />
            </div>

            <div
                style={{
                    marginTop: 12,
                    display: "grid",
                    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                    gap: 8,
                }}
            >
                <MiniMetric label="Pend." value={reviewer.pending_reviews || 0} danger={safeNumber(reviewer.pending_reviews) > 0} />
                <MiniMetric label="Aprob." value={reviewer.approved_reviews || 0} />
                <MiniMetric label="Dev." value={reviewer.returned_reviews || 0} />
                <MiniMetric label="Venc." value={reviewer.overdue_review_tasks || 0} danger={safeNumber(reviewer.overdue_review_tasks) > 0} />
            </div>

            <div
                style={{
                    marginTop: 12,
                    borderRadius: 16,
                    background: s.bg,
                    border: `1px solid ${s.border}`,
                    color: s.color,
                    padding: 10,
                    fontSize: 12,
                    fontWeight: 900,
                    lineHeight: 1.35,
                }}
            >
                {cleanText(reviewer.pressure_label || "Sin presión")}
                <button
                    type="button"
                    onClick={() => onOpenReviewerTasks?.(reviewer)}
                    style={{
                        marginTop: 10,
                        width: "100%",
                        height: 36,
                        borderRadius: 999,
                        border: "1px solid #0F172A",
                        background: "#0F172A",
                        color: "#FFFFFF",
                        fontSize: 11,
                        fontWeight: 950,
                        cursor: "pointer",
                        boxShadow: "0 10px 22px rgba(15,23,42,.16)",
                    }}
                >
                    Ver tareas bajo revisión →
                </button>
            </div>
        </div>
    );
}

function MiniMetric({ label, value, danger = false }) {
    return (
        <div
            style={{
                borderRadius: 14,
                background: danger ? "#FFF1F2" : "#F8FAFC",
                border: danger ? "1px solid #FECDD3" : "1px solid #E2E8F0",
                padding: "9px 8px",
                textAlign: "center",
            }}
        >
            <div style={{ color: danger ? "#BE123C" : "#64748B", fontSize: 9, fontWeight: 950, textTransform: "uppercase" }}>
                {label}
            </div>
            <div style={{ marginTop: 3, color: danger ? "#BE123C" : "#0F172A", fontSize: 15, fontWeight: 950 }}>
                {value}
            </div>
        </div>
    );
}

function BottleneckRow({ task, onOpen }) {
    const pr = priorityStyle(task.priority);
    const score = safeNumber(task.bottleneck_score);

    return (
        <button
            type="button"
            onClick={() => onOpen?.(task)}
            style={{
                width: "100%",
                textAlign: "left",
                border: score >= 90 ? "1px solid #FCA5A5" : "1px solid #E2E8F0",
                background: score >= 90 ? "#FFF7F7" : "#FFFFFF",
                borderRadius: 18,
                padding: 14,
                cursor: "pointer",
                boxShadow: "0 10px 24px rgba(15,23,42,.04)",
            }}
        >
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
                <div style={{ minWidth: 0 }}>
                    <div
                        style={{
                            color: "#0F172A",
                            fontSize: 13,
                            fontWeight: 950,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                        }}
                    >
                        {task.wbs_code ? `${task.wbs_code} · ` : ""}{task.task_name || "Tarea"}
                    </div>
                    <div style={{ marginTop: 5, color: "#64748B", fontSize: 11, fontWeight: 800 }}>
                        {task.project_name || "Sin proyecto"} · {task.owner_name || "Sin responsable"}
                    </div>
                </div>

                <div
                    style={{
                        minWidth: 72,
                        textAlign: "center",
                        borderRadius: 14,
                        background: score >= 90 ? "#FFF1F2" : score >= 75 ? "#FFF7ED" : "#FFFBEB",
                        border: score >= 90 ? "1px solid #FECDD3" : score >= 75 ? "1px solid #FDBA74" : "1px solid #FDE68A",
                        color: score >= 90 ? "#BE123C" : score >= 75 ? "#C2410C" : "#B45309",
                        padding: "7px 8px",
                        fontWeight: 950,
                    }}
                >
                    <div style={{ fontSize: 18, letterSpacing: "-.03em", lineHeight: 1 }}>
                        {score.toFixed(0)}
                    </div>
                    <div
                        style={{
                            marginTop: 5,
                            fontSize: 9,
                            fontWeight: 950,
                            color: "#1D4ED8",
                            whiteSpace: "nowrap",
                        }}
                    >
                        Abrir →
                    </div>
                </div>
            </div>

            <div style={{ marginTop: 10, display: "flex", gap: 7, flexWrap: "wrap" }}>
                <span
                    title={getPmoTooltip(label(task.priority))}
                    style={{ ...pillBase, background: pr.bg, color: pr.color, border: `1px solid ${pr.border}` }}
                >
                    {label(task.priority)}
                </span>
                <span
                    title={`${getPmoTooltip(getTaskStatusLabel(task.status))} Avance actual: ${safeNumber(task.progress_percent).toFixed(0)}%.`}
                    style={{ ...pillBase, background: "#EFF6FF", color: "#1D4ED8", border: "1px solid #BFDBFE" }}
                >
                    {getTaskStatusLabel(task.status)} · {safeNumber(task.progress_percent).toFixed(0)}%
                </span>
                {safeNumber(task.days_overdue) > 0 ? (
                    <span
                        title="Indica atraso respecto a la fecha objetivo de la tarea."
                        style={{ ...pillBase, background: "#FFF1F2", color: "#BE123C", border: "1px solid #FECDD3" }}
                    >
                        {safeNumber(task.days_overdue)} días vencida
                    </span>
                ) : null}
                {task.reviewer_name ? (
                    <span
                        title="Persona asignada para validar o aprobar el avance."
                        style={{ ...pillBase, background: "#F8FAFC", color: "#475569", border: "1px solid #E2E8F0" }}
                    >
                        Revisa: {task.reviewer_name}
                    </span>
                ) : (
                    <span
                        title="La tarea necesita un responsable de validación para cerrar el flujo."
                        style={{ ...pillBase, background: "#FFFBEB", color: "#B45309", border: "1px solid #FDE68A" }}
                    >
                        Sin revisor
                    </span>
                )}
            </div>
        </button>
    );
}

function buildReviewPressureInsight(reviewers = []) {
    if (!reviewers.length) {
        return "No hay revisores con carga activa en esta consulta.";
    }

    const highest = [...reviewers].sort(
        (a, b) => safeNumber(b.review_pressure_score) - safeNumber(a.review_pressure_score)
    )[0];

    const totalPending = reviewers.reduce((acc, r) => acc + safeNumber(r.pending_reviews), 0);
    const totalOverdue = reviewers.reduce((acc, r) => acc + safeNumber(r.overdue_review_tasks), 0);
    const totalReturned = reviewers.reduce((acc, r) => acc + safeNumber(r.returned_reviews), 0);

    if (totalPending === 0 && totalOverdue > 0) {
        return `La presión actual no proviene de revisiones pendientes activas, sino de ${totalOverdue} tarea(s) vencida(s) asociadas a revisores. Mayor presión: ${highest?.display_name || "sin revisor"} (${safeNumber(highest?.review_pressure_score).toFixed(0)} pts).`;
    }

    if (totalPending > 0) {
        return `Existen ${totalPending} revisión(es) pendiente(s) que requieren decisión formal. Prioriza al revisor con mayor presión: ${highest?.display_name || "sin revisor"} (${safeNumber(highest?.review_pressure_score).toFixed(0)} pts).`;
    }

    if (totalReturned > 0) {
        return `Hay ${totalReturned} tarea(s) devuelta(s) o con cambios. Conviene revisar si el responsable ya atendió las correcciones solicitadas.`;
    }

    return "El flujo de revisión se mantiene estable. No se observa acumulación relevante de pendientes, vencimientos o devoluciones.";
}

function buildBottleneckInsight(bottlenecks = []) {
    if (!bottlenecks.length) {
        return "No se detectan cuellos de botella relevantes en esta consulta.";
    }

    const critical = bottlenecks.filter((t) => safeNumber(t.bottleneck_score) >= 90).length;
    const high = bottlenecks.filter((t) => safeNumber(t.bottleneck_score) >= 75 && safeNumber(t.bottleneck_score) < 90).length;
    const overdue = bottlenecks.filter((t) => safeNumber(t.days_overdue) > 0).length;
    const noReviewer = bottlenecks.filter((t) => !t.reviewer_name).length;
    const inconsistent = bottlenecks.filter((t) => {
        const progress = safeNumber(t.progress_percent);
        const status = String(t.status || "").toLowerCase();
        return (status === "pendiente" && progress >= 80) || (status !== "terminada" && progress >= 100);
    }).length;

    const top = bottlenecks[0];

    if (critical > 0) {
        return `Hay ${critical} cuello(s) crítico(s). El principal es ${top?.wbs_code ? `${top.wbs_code} · ` : ""}${top?.task_name || "una tarea sin nombre"}. La prioridad PMO debe ser corregir vencimiento, revisor, evidencia o cierre formal antes de abrir nuevas cargas.`;
    }

    if (high > 0) {
        return `Hay ${high} cuello(s) alto(s). La operación avanza, pero existen tareas con fricción suficiente para afectar fechas, revisiones o cierres.`;
    }

    if (inconsistent > 0) {
        return `Se detectan ${inconsistent} tarea(s) con avance inconsistente. Probablemente no falta trabajo técnico, sino cierre, evidencia, revisión o actualización de estado.`;
    }

    if (noReviewer > 0) {
        return `Hay ${noReviewer} tarea(s) dentro del ranking sin revisor. Sin validación formal, el avance puede quedar sin cierre PMO.`;
    }

    if (overdue > 0) {
        return `Los cuellos actuales se explican principalmente por tareas vencidas. Revisa si deben cerrarse, reprogramarse o reasignarse.`;
    }

    return "Los cuellos detectados son de seguimiento moderado. Mantén revisión preventiva para evitar acumulación de atraso.";
}

function GuidePill({ children, tone = "neutral", title }) {
    const styles = PMO_BADGE_TONES;
    const s = styles[tone] || styles.neutral;
    const tooltip = title || getPmoTooltip(children);

    return (
        <span
            title={tooltip}
            style={{
                display: "inline-flex",
                alignItems: "center",
                padding: "6px 9px",
                borderRadius: 999,
                background: s.bg,
                color: s.color,
                border: `1px solid ${s.border}`,
                fontSize: 10,
                fontWeight: 950,
                whiteSpace: "nowrap",
            }}
        >
            {children}
        </span>
    );
}

function InsightBox({ children, tone = "info" }) {
    const styles = {
        info: { bg: "#EFF6FF", color: "#1D4ED8", border: "#BFDBFE", icon: "◉" },
        warning: { bg: "#FFFBEB", color: "#B45309", border: "#FDE68A", icon: "◐" },
        danger: { bg: "#FFF1F2", color: "#BE123C", border: "#FECDD3", icon: "!" },
    };

    const s = styles[tone] || styles.info;

    return (
        <div
            style={{
                marginTop: 12,
                borderRadius: 18,
                background: s.bg,
                border: `1px solid ${s.border}`,
                color: s.color,
                padding: 12,
                fontSize: 12,
                fontWeight: 850,
                lineHeight: 1.45,
                display: "flex",
                gap: 9,
                alignItems: "flex-start",
            }}
        >
            <span style={{ fontWeight: 950 }}>{s.icon}</span>
            <span>{children}</span>
        </div>
    );
}

const pillBase = {
    display: "inline-flex",
    alignItems: "center",
    padding: "6px 9px",
    borderRadius: 999,
    fontSize: 10,
    fontWeight: 950,
    whiteSpace: "nowrap",
};


function ScopeNotice({ scope, fallbackLabel, fallbackDescription }) {
    const labelText = scope?.label || fallbackLabel;
    const descriptionText = scope?.description || fallbackDescription;

    return (
        <div
            style={{
                margin: "0 0 18px",
                borderRadius: 20,
                background: "#FFFFFF",
                border: "1px solid #DBEAFE",
                padding: "14px 16px",
                boxShadow: "0 12px 28px rgba(37,99,235,.06)",
                display: "flex",
                gap: 12,
                alignItems: "flex-start",
            }}
        >
            <div
                style={{
                    width: 34,
                    height: 34,
                    borderRadius: 14,
                    background: "#EFF6FF",
                    color: "#1D4ED8",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 950,
                    flex: "0 0 auto",
                }}
            >
                ⌖
            </div>
            <div>
                <div style={{ color: "#0F172A", fontSize: 13, fontWeight: 950 }}>
                    Alcance de la vista: {labelText}
                </div>
                <div style={{ marginTop: 4, color: "#64748B", fontSize: 12, fontWeight: 750, lineHeight: 1.45 }}>
                    {descriptionText}
                </div>
            </div>
        </div>
    );
}

export default function ProductivityPage() {
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [data, setData] = useState(null);
    const [selectedProjectId, setSelectedProjectId] = useState("all");

    const loadProductivity = async () => {
        try {
            setLoading(true);
            setError("");

            const token = localStorage.getItem("cani_auth") || "";
            const qp = new URLSearchParams();

            if (selectedProjectId && selectedProjectId !== "all") {
                qp.set("project_id", selectedProjectId);
            }

            const response = await fetch(`${API_BASE}/pmo_productivity.php${qp.toString() ? `?${qp.toString()}` : ""}`, {
                headers: {
                    Authorization: token ? `Bearer ${token}` : "",
                    "X-CANI-AUTH": token || "",
                },
            });

            const text = await response.text();

            if (!text || !text.trim()) {
                throw new Error("Respuesta vacía de Productividad PMO");
            }

            const json = JSON.parse(text);

            if (!response.ok || !json.success) {
                throw new Error(json.message || json.detail || "No se pudo cargar productividad PMO");
            }

            setData(json.data || null);
        } catch (e) {
            setError(e.message || "Error al cargar productividad PMO");
        } finally {
            setLoading(false);
        }
    };

    function buildExecutiveInsight({ summary, responsibles, reviewers, bottlenecks, selectedProjectName }) {
        const totalTasks = safeNumber(summary.total_tasks);
        const overdueTasks = safeNumber(summary.overdue_tasks);
        const inconsistentTasks = safeNumber(summary.inconsistent_tasks);
        const bottlenecksCount = safeNumber(summary.bottlenecks_count);
        const completedTasks = safeNumber(summary.completed_tasks);

        const avgReliability = responsibles.length
            ? responsibles.reduce((acc, r) => acc + safeNumber(r.reliability_index), 0) / responsibles.length
            : 0;

        const worstResponsible = [...responsibles]
            .sort((a, b) => safeNumber(a.reliability_index) - safeNumber(b.reliability_index))[0];

        const highestPressureReviewer = [...reviewers]
            .sort((a, b) => safeNumber(b.review_pressure_score) - safeNumber(a.review_pressure_score))[0];

        const topBottleneck = bottlenecks?.[0] || null;

        const overdueRate = totalTasks > 0 ? (overdueTasks / totalTasks) * 100 : 0;
        const completedRate = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;

        let status = "stable";
        let title = "Flujo operativo estable";
        let message = "La operación muestra una condición controlada. Mantén seguimiento preventivo sobre vencimientos, revisiones y evidencias.";
        let action = "Mantener monitoreo semanal y cerrar tareas próximas a vencer.";
        let tone = {
            bg: "#ECFDF5",
            border: "#A7F3D0",
            color: "#047857",
            icon: "✓",
        };

        if (avgReliability < 65 || overdueRate >= 30 || bottlenecksCount >= 8) {
            status = "critical";
            title = "Confiabilidad operativa en atención";
            message = "La confiabilidad está baja principalmente por deuda operativa: tareas vencidas, avance inconsistente y cierres pendientes. Esto no necesariamente indica falta de trabajo, sino falta de cierre, actualización o validación formal.";
            action = "Priorizar tareas vencidas, corregir avances inconsistentes y cerrar tareas con avance alto antes de abrir nuevas cargas.";
            tone = {
                bg: "#FFF1F2",
                border: "#FECDD3",
                color: "#BE123C",
                icon: "!",
            };
        } else if (avgReliability < 80 || overdueRate >= 15 || inconsistentTasks > 0) {
            status = "warning";
            title = "Confiabilidad media con riesgos operativos";
            message = "El flujo tiene avance, pero existen señales de fricción: tareas vencidas, inconsistencias entre avance y estado, o revisiones acumuladas.";
            action = "Revisar responsables con menor Reliability Index y resolver los principales cuellos de botella.";
            tone = {
                bg: "#FFFBEB",
                border: "#FDE68A",
                color: "#B45309",
                icon: "◐",
            };
        }

        const bullets = [];

        if (overdueTasks > 0) {
            bullets.push(`${overdueTasks} tarea(s) vencida(s), equivalentes al ${overdueRate.toFixed(0)}% del total operativo.`);
        }

        if (inconsistentTasks > 0) {
            bullets.push(`${inconsistentTasks} tarea(s) con avance inconsistente: avance alto, pero sin cierre operativo formal.`);
        }

        if (worstResponsible) {
            bullets.push(`Responsable con menor confiabilidad: ${worstResponsible.display_name} (${safeNumber(worstResponsible.reliability_index).toFixed(1)}/100).`);
        }

        if (highestPressureReviewer) {
            bullets.push(`Mayor presión de revisión: ${highestPressureReviewer.display_name} (${safeNumber(highestPressureReviewer.review_pressure_score).toFixed(0)} pts).`);
        }

        if (topBottleneck) {
            bullets.push(`Principal cuello de botella: ${topBottleneck.wbs_code ? `${topBottleneck.wbs_code} · ` : ""}${topBottleneck.task_name}.`);
        }

        if (!bullets.length) {
            bullets.push("No se detectan fricciones críticas en la consulta actual.");
        }

        return {
            status,
            title,
            message,
            action,
            bullets,
            tone,
            selectedProjectName: selectedProjectName || PMO_SCOPE_LABELS.portfolio,
            avgReliability,
            completedRate,
            overdueRate,
        };
    }

    useEffect(() => {
        loadProductivity();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedProjectId]);

    const summary = data?.summary || {};
    const scope = data?.scope || {};
    const projects = data?.project_options || data?.projects || [];
    const responsibles = data?.responsibles || [];
    const reviewers = data?.reviewers || [];
    const bottlenecks = data?.bottlenecks || [];
    const distribution = data?.reliability_distribution || {};
    const reviewPressureInsight = useMemo(() => {
        return buildReviewPressureInsight(reviewers);
    }, [reviewers]);

    const bottleneckInsight = useMemo(() => {
        return buildBottleneckInsight(bottlenecks);
    }, [bottlenecks]);

    const selectedProjectName = useMemo(() => {
        if (selectedProjectId === "all") return getScopeLabel(selectedProjectId);

        const found = projects.find((p) => String(p.id || p.project_id) === String(selectedProjectId));

        return found ? `${found.code || "PRJ"} · ${found.name}` : getScopeLabel(selectedProjectId);
    }, [projects, selectedProjectId]);

    const executiveInsight = useMemo(() => {
        return buildExecutiveInsight({
            summary,
            responsibles,
            reviewers,
            bottlenecks,
            selectedProjectName,
        });
    }, [summary, responsibles, reviewers, bottlenecks, selectedProjectName]);

    const avgReliability = useMemo(() => {
        if (!responsibles.length) return 0;
        const total = responsibles.reduce((acc, r) => acc + safeNumber(r.reliability_index), 0);
        return total / responsibles.length;
    }, [responsibles]);

    const openTask = (task) => {
        if (!task?.task_id) return;

        const qp = new URLSearchParams();
        qp.set("mode", "progress");
        qp.set("return_to", selectedProjectId !== "all" ? `/productivity?project_id=${selectedProjectId}` : "/productivity");
        qp.set("return_label", "Productividad PMO");

        navigate(`/my-work/task/${task.task_id}?${qp.toString()}`);
    };

    const openReviewerTasks = (reviewer) => {
        if (!reviewer?.user_id) return;

        const qp = new URLSearchParams();

        qp.set("quick", "overdue");
        qp.set("reviewer_id", reviewer.user_id);
        qp.set("return_to", selectedProjectId !== "all" ? `/productivity?project_id=${selectedProjectId}` : "/productivity");
        qp.set("return_label", "Productividad PMO");

        if (selectedProjectId && selectedProjectId !== "all") {
            qp.set("project_id", selectedProjectId);
        }

        navigate(`/kanban?${qp.toString()}`);
    };

    if (loading) {
        return (
            <div
                style={{
                    minHeight: "60vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#64748B",
                    fontSize: 14,
                    fontWeight: 850,
                }}
            >
                Cargando Productividad PMO...
            </div>
        );
    }

    if (error) {
        return (
            <div
                style={{
                    borderRadius: 20,
                    background: "#FFF1F2",
                    border: "1px solid #FECDD3",
                    color: "#BE123C",
                    padding: 20,
                    fontSize: 14,
                    fontWeight: 850,
                }}
            >
                {error}
            </div>
        );
    }

    return (
        <div
            style={{
                minHeight: "100vh",
                background: "radial-gradient(circle at top left, rgba(37,99,235,.10), transparent 28%), #F8FAFC",
            }}
        >
            <section
                style={{
                    marginBottom: 18,
                    borderRadius: 28,
                    padding: 26,
                    background: "linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 58%, #EEF2FF 100%)",
                    border: "1px solid #E2E8F0",
                    boxShadow: "0 20px 50px rgba(15,23,42,.07)",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 20,
                    flexWrap: "wrap",
                }}
            >
                <div>
                    <div
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 8,
                            padding: "7px 11px",
                            borderRadius: 999,
                            background: "#EFF6FF",
                            border: "1px solid #BFDBFE",
                            color: "#1D4ED8",
                            fontSize: 11,
                            fontWeight: 950,
                            letterSpacing: ".06em",
                            textTransform: "uppercase",
                            marginBottom: 14,
                        }}
                    >
                        <span style={{ width: 8, height: 8, borderRadius: 999, background: "#2563EB" }} />
                        Productividad / Confiabilidad / PMO
                    </div>

                    <h1
                        style={{
                            margin: 0,
                            fontSize: 34,
                            lineHeight: 1.05,
                            color: "#0F172A",
                            fontWeight: 950,
                            letterSpacing: "-.05em",
                        }}
                    >
                        Productividad PMO
                    </h1>

                    <p
                        style={{
                            margin: "10px 0 0",
                            color: "#64748B",
                            fontSize: 14,
                            lineHeight: 1.5,
                            maxWidth: 840,
                            fontWeight: 700,
                        }}
                    >
                        Analiza todas las tareas del proyecto o portafolio seleccionado, agrupadas por responsable, revisor, confiabilidad, presión de revisión y cuellos de botella PMO.
                    </p>
                </div>

                <div
                    style={{
                        minWidth: 260,
                        borderRadius: 24,
                        background: "#0F172A",
                        color: "#FFFFFF",
                        padding: 18,
                        boxShadow: "0 20px 45px rgba(15,23,42,.22)",
                    }}
                >
                    <div style={{ color: "#94A3B8", fontSize: 11, fontWeight: 950, textTransform: "uppercase" }}>
                        Reliability Index promedio
                    </div>
                    <div style={{ marginTop: 8, fontSize: 42, fontWeight: 950, letterSpacing: "-.06em" }}>
                        {avgReliability.toFixed(0)}
                    </div>
                    <div style={{ marginTop: 6, color: "#CBD5E1", fontSize: 12, fontWeight: 800 }}>
                        {distribution.verde || 0} verde · {distribution.amarillo || 0} amarillo · {distribution.rojo || 0} rojo
                    </div>
                </div>
            </section>

            <ScopeNotice
                scope={scope}
                fallbackLabel="Proyecto completo / Portafolio PMO"
                fallbackDescription="Analiza todas las tareas del proyecto o portafolio seleccionado, agrupadas por responsable, revisor y friccion operativa."
            />

            <section
                style={{
                    marginBottom: 18,
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 12,
                    alignItems: "center",
                    flexWrap: "wrap",
                }}
            >
                <select
                    value={selectedProjectId}
                    onChange={(e) => setSelectedProjectId(e.target.value)}
                    style={{
                        height: 42,
                        minWidth: 320,
                        borderRadius: 999,
                        border: "1px solid #E2E8F0",
                        background: "#FFFFFF",
                        padding: "0 16px",
                        color: "#0F172A",
                        fontSize: 13,
                        fontWeight: 800,
                        outline: "none",
                        boxShadow: "0 12px 25px rgba(15,23,42,.04)",
                    }}
                >
                    <option value="all">Portafolio PMO</option>
                    {projects.map((p) => (
                        <option key={p.id || p.project_id} value={p.id || p.project_id}>
                            {p.code || "PRJ"} · {p.name || p.project_name}
                        </option>
                    ))}
                </select>

                <button
                    type="button"
                    onClick={loadProductivity}
                    style={{
                        height: 42,
                        padding: "0 16px",
                        borderRadius: 999,
                        border: "1px solid #0F172A",
                        background: "#0F172A",
                        color: "#FFFFFF",
                        fontSize: 12,
                        fontWeight: 950,
                        cursor: "pointer",
                        boxShadow: "0 14px 28px rgba(15,23,42,.18)",
                    }}
                >
                    Actualizar
                </button>
            </section>

            <section
                style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                    gap: 16,
                    marginBottom: 18,
                }}
            >
                <KpiCard title="Tareas" value={summary.total_tasks || 0} subtitle="Total operativo" color="#0F172A" icon="▦" />
                <KpiCard title="Terminadas" value={summary.completed_tasks || 0} subtitle="Cierre acumulado" color="#16A34A" icon="✓" />
                <KpiCard title="Vencidas" value={summary.overdue_tasks || 0} subtitle="Presión operativa" color="#DC2626" icon="!" />
                <KpiCard title="Críticas" value={summary.critical_tasks || 0} subtitle="Prioridad alta PMO" color="#BE123C" icon="▲" />
                <KpiCard title="Inconsistentes" value={summary.inconsistent_tasks || 0} subtitle="Avance vs estado" color="#F59E0B" icon="◐" />
                <KpiCard title="Cuellos" value={summary.bottlenecks_count || 0} subtitle="Tareas con fricción" color="#7C3AED" icon="⌁" />
            </section>
            <section
                style={{
                    marginBottom: 18,
                    borderRadius: 28,
                    background: executiveInsight.tone.bg,
                    border: `1px solid ${executiveInsight.tone.border}`,
                    padding: 22,
                    boxShadow: "0 18px 45px rgba(15,23,42,.06)",
                }}
            >
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 18,
                        alignItems: "flex-start",
                        flexWrap: "wrap",
                    }}
                >
                    <div style={{ maxWidth: 880 }}>
                        <div
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 8,
                                padding: "6px 10px",
                                borderRadius: 999,
                                background: "#FFFFFF",
                                border: `1px solid ${executiveInsight.tone.border}`,
                                color: executiveInsight.tone.color,
                                fontSize: 11,
                                fontWeight: 950,
                                textTransform: "uppercase",
                                letterSpacing: ".04em",
                                marginBottom: 12,
                            }}
                        >
                            <span>{executiveInsight.tone.icon}</span>
                            Lectura PMO automática
                        </div>

                        <h2
                            style={{
                                margin: 0,
                                color: executiveInsight.tone.color,
                                fontSize: 22,
                                fontWeight: 950,
                                letterSpacing: "-.03em",
                            }}
                        >
                            {executiveInsight.title}
                        </h2>

                        <div
                            style={{
                                marginTop: 6,
                                color: "#334155",
                                fontSize: 12,
                                fontWeight: 850,
                            }}
                        >
                            Consulta actual: {executiveInsight.selectedProjectName}
                        </div>

                        <p
                            style={{
                                margin: "12px 0 0",
                                color: "#334155",
                                fontSize: 14,
                                lineHeight: 1.55,
                                fontWeight: 750,
                            }}
                        >
                            {executiveInsight.message}
                        </p>

                        <div
                            style={{
                                marginTop: 14,
                                display: "grid",
                                gap: 8,
                            }}
                        >
                            {executiveInsight.bullets.map((item, index) => (
                                <div
                                    key={index}
                                    style={{
                                        display: "flex",
                                        gap: 8,
                                        alignItems: "flex-start",
                                        color: "#334155",
                                        fontSize: 13,
                                        fontWeight: 800,
                                        lineHeight: 1.35,
                                    }}
                                >
                                    <span
                                        style={{
                                            color: executiveInsight.tone.color,
                                            fontWeight: 950,
                                        }}
                                    >
                                        •
                                    </span>
                                    <span>{item}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div
                        style={{
                            minWidth: 250,
                            borderRadius: 22,
                            background: "#FFFFFF",
                            border: `1px solid ${executiveInsight.tone.border}`,
                            padding: 16,
                            boxShadow: "0 12px 28px rgba(15,23,42,.05)",
                        }}
                    >
                        <div
                            style={{
                                color: "#64748B",
                                fontSize: 10,
                                fontWeight: 950,
                                textTransform: "uppercase",
                                letterSpacing: ".04em",
                            }}
                        >
                            Acción sugerida
                        </div>

                        <div
                            style={{
                                marginTop: 8,
                                color: "#0F172A",
                                fontSize: 14,
                                fontWeight: 900,
                                lineHeight: 1.45,
                            }}
                        >
                            {executiveInsight.action}
                        </div>

                        <div
                            style={{
                                marginTop: 14,
                                display: "grid",
                                gridTemplateColumns: "repeat(2, 1fr)",
                                gap: 8,
                            }}
                        >
                            <MiniMetric
                                label="RI Prom."
                                value={executiveInsight.avgReliability.toFixed(0)}
                                danger={executiveInsight.avgReliability < 65}
                            />
                            <MiniMetric
                                label="Venc."
                                value={`${executiveInsight.overdueRate.toFixed(0)}%`}
                                danger={executiveInsight.overdueRate >= 30}
                            />
                        </div>
                    </div>
                </div>
            </section>
            <section
                style={{
                    marginBottom: 18,
                    borderRadius: 22,
                    background: "#FFFFFF",
                    border: "1px solid #E2E8F0",
                    padding: 16,
                    boxShadow: "0 12px 28px rgba(15,23,42,.04)",
                }}
            >
                <div
                    style={{
                        display: "flex",
                        gap: 12,
                        alignItems: "flex-start",
                    }}
                >
                    <div
                        style={{
                            width: 34,
                            height: 34,
                            borderRadius: 14,
                            background: "#EFF6FF",
                            color: "#1D4ED8",
                            border: "1px solid #BFDBFE",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 16,
                            fontWeight: 950,
                            flex: "0 0 auto",
                        }}
                    >
                        i
                    </div>

                    <div>
                        <div
                            style={{
                                color: "#0F172A",
                                fontSize: 13,
                                fontWeight: 950,
                                marginBottom: 5,
                            }}
                        >
                            Criterio PMO de interpretación
                        </div>

                        <div
                            style={{
                                color: "#475569",
                                fontSize: 12,
                                fontWeight: 750,
                                lineHeight: 1.55,
                            }}
                        >
                            Productividad PMO mide el riesgo por rol dentro del flujo de gobierno.
                            <strong> Presión de revisión </strong>
                            mide tareas donde el usuario participa como revisor;
                            <strong> Mi Trabajo </strong>
                            muestra la carga total del usuario como responsable y/o revisor.
                            Si los números no coinciden, la diferencia ayuda a identificar si el atraso viene de ejecución, revisión o gobierno del proyecto.
                        </div>
                    </div>
                </div>
            </section>
            <section
                style={{
                    display: "grid",
                    gridTemplateColumns: "minmax(0, 1fr) minmax(360px, .8fr)",
                    gap: 18,
                    alignItems: "start",
                    marginBottom: 18,
                }}
            >
                <ReliabilityHorizontalChart items={responsibles} />

                <div
                    style={{
                        borderRadius: 28,
                        background: "rgba(255,255,255,.92)",
                        border: "1px solid #E2E8F0",
                        padding: 18,
                        boxShadow: "0 18px 45px rgba(15,23,42,.06)",
                    }}
                >
                    <div style={{ marginBottom: 14 }}>
                        <div style={{ marginBottom: 14 }}>
                            <SectionTitle
                                title="Presión de revisión"
                                subtitle="Carga de validación, revisiones pendientes y presión acumulada."
                            />

                            <div
                                style={{
                                    marginTop: -6,
                                    display: "flex",
                                    gap: 8,
                                    flexWrap: "wrap",
                                    alignItems: "center",
                                }}
                            >
                                <span
                                    style={{
                                        fontSize: 10,
                                        fontWeight: 950,
                                        color: "#64748B",
                                        textTransform: "uppercase",
                                        letterSpacing: ".04em",
                                    }}
                                >
                                    Guía:
                                </span>

                                <GuidePill tone="success">0–30 estable</GuidePill>
                                <GuidePill tone="warning">31–79 presión moderada</GuidePill>
                                <GuidePill tone="danger">80+ cuello crítico</GuidePill>
                            </div>

                            <InsightBox tone="warning">
                                {reviewPressureInsight}
                            </InsightBox>
                        </div>
                        <div
                            style={{
                                marginTop: -6,
                                display: "flex",
                                gap: 8,
                                flexWrap: "wrap",
                                alignItems: "center",
                            }}
                        >
                            <span
                                style={{
                                    fontSize: 10,
                                    fontWeight: 950,
                                    color: "#64748B",
                                    textTransform: "uppercase",
                                    letterSpacing: ".04em",
                                }}
                            >
                                Guía:
                            </span>

                            <span style={{ ...pillBase, background: "#ECFDF5", color: "#047857", border: "1px solid #A7F3D0" }}>
                                0–30 estable
                            </span>

                            <span style={{ ...pillBase, background: "#FFFBEB", color: "#B45309", border: "1px solid #FDE68A" }}>
                                31–79 presión moderada
                            </span>

                            <span style={{ ...pillBase, background: "#FFF1F2", color: "#BE123C", border: "1px solid #FECDD3" }}>
                                80+ cuello crítico
                            </span>
                        </div>
                    </div>
                    <div style={{ display: "grid", gap: 12 }}>
                        {reviewers.length ? reviewers.map((reviewer) => (
                            <ReviewerCard
                                key={reviewer.user_id}
                                reviewer={reviewer}
                                onOpenReviewerTasks={openReviewerTasks}
                            />
                        )) : <EmptyBox text="No hay revisores con tareas asignadas para este filtro." />}
                    </div>
                </div>
            </section>

            <section
                style={{
                    borderRadius: 28,
                    background: "rgba(255,255,255,.92)",
                    border: "1px solid #E2E8F0",
                    padding: 18,
                    boxShadow: "0 18px 45px rgba(15,23,42,.06)",
                }}
            >
                <div style={{ marginBottom: 14 }}>
                    <SectionTitle
                        title="Ranking de cuellos de botella"
                        subtitle="Tareas que concentran atraso, prioridad crítica, revisión pendiente o avance inconsistente."
                    />

                    <div
                        style={{
                            marginTop: -6,
                            display: "flex",
                            gap: 8,
                            flexWrap: "wrap",
                            alignItems: "center",
                        }}
                    >
                        <span
                            style={{
                                fontSize: 10,
                                fontWeight: 950,
                                color: "#64748B",
                                textTransform: "uppercase",
                                letterSpacing: ".04em",
                            }}
                        >
                            Guía:
                        </span>

                        <GuidePill tone="neutral">0–49 seguimiento</GuidePill>
                        <GuidePill tone="warning">50–74 moderado</GuidePill>
                        <GuidePill tone="orange">75–89 alto</GuidePill>
                        <GuidePill tone="danger">90+ crítico</GuidePill>
                    </div>

                    <InsightBox tone="danger">
                        {bottleneckInsight}
                    </InsightBox>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(330px, 1fr))", gap: 12 }}>
                    {bottlenecks.length ? bottlenecks.map((task) => (
                        <BottleneckRow key={task.task_id} task={task} onOpen={openTask} />
                    )) : <EmptyBox text="No se detectaron cuellos de botella para este filtro." />}
                </div>
            </section>
        </div>
    );
}

function SectionTitle({ title, subtitle }) {
    return (
        <div style={{ marginBottom: 14 }}>
            <h2
                style={{
                    margin: 0,
                    color: "#0F172A",
                    fontSize: 18,
                    fontWeight: 950,
                    letterSpacing: "-.03em",
                }}
            >
                {title}
            </h2>
            <div style={{ marginTop: 5, color: "#64748B", fontSize: 12, fontWeight: 750, lineHeight: 1.45 }}>
                {subtitle}
            </div>
        </div>
    );
}

function EmptyBox({ text }) {
    return (
        <div
            style={{
                borderRadius: 18,
                border: "1px dashed #CBD5E1",
                background: "#F8FAFC",
                padding: 16,
                color: "#64748B",
                fontSize: 12,
                fontWeight: 800,
                textAlign: "center",
            }}
        >
            {text}
        </div>
    );
}
